import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

export default function About() {
    return (
        <>
   <Helmet>

  {/* BASIC SEO */}
  <html lang="en" />

  <title>
    Neuralis Reflex Game | Reaction Time, Focus & Memory Training | OMORE Games
  </title>

  <meta
    name="description"
    content="Neuralis is a fast reflex and brain training game by OMORE Games. Test reaction time, focus, and memory with real-time cognitive challenges in your browser."
  />

  <meta
    name="keywords"
    content="
    neuralis game,
    reaction time test,
    reflex test online,
    brain training game,
    focus game,
    memory game,
    cognitive training,
    browser brain game,
    reflex speed test,
    omore games
    "
  />

  <meta name="robots" content="index, follow" />

  <meta name="author" content="OMORE Games" />

  <link rel="canonical" href="https://omore.games/" />


  {/* OPEN GRAPH */}
  <meta property="og:type" content="website" />
  <meta property="og:title" content="Neuralis Reflex Game | OMORE Games" />
  <meta
    property="og:description"
    content="Play Neuralis online. Train reflex speed, focus, and memory with fast brain challenges."
  />
  <meta property="og:url" content="https://omore.games/" />
  <meta property="og:site_name" content="OMORE Games" />

  <meta
    property="og:image"
    content="https://omore.games/og-cover.png"
  />


  {/* TWITTER */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Neuralis Reflex Game" />
  <meta
    name="twitter:description"
    content="Reaction speed, focus, and memory training game playable in browser."
  />
  <meta
    name="twitter:image"
    content="https://omore.games/og-cover.png"
  />


  {/* VIDEO GAME SCHEMA */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "VideoGame",
      name: "Neuralis",
      url: "https://omore.games",
      genre: "Brain Game",
      gamePlatform: "Web Browser",
      applicationCategory: "Game",
      operatingSystem: "Any",
      creator: {
        "@type": "Organization",
        name: "OMORE Games"
      },
      author: {
        "@type": "Person",
        name: "Omkar More"
      },
      description:
        "Neuralis is a reflex and cognitive training game designed to test reaction time, focus, and memory.",
      inLanguage: "en"
    })}
  </script>


  {/* BREADCRUMB SCHEMA */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://omore.games"
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Neuralis",
          item: "https://omore.games"
        }
      ]
    })}
  </script>


</Helmet>

            <section className="relative bg-[#111214] text-white py-20 px-6 overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />

                <div className="max-w-5xl mx-auto">
                    {/* SEO Header */}
                    <header className="mb-20">
                        <h2 className="text-[10px] md:text-xs tracking-[0.8em] text-blue-500 uppercase font-black mb-4">
                            About Neuralis
                        </h2>

                        <h1 className="text-4xl md:text-7xl font-black tracking-tighter mb-6">
                            Train Your <span className="text-blue-500">Neural</span> Speed
                        </h1>

                        <p className="text-zinc-400 text-sm md:text-lg max-w-3xl leading-relaxed tracking-wide">
                            Neuralis is a modern reflex training game developed by OMORE Games.
                            It is designed to help players improve their reaction speed,
                            focus, and memory through fast cognitive challenges and visual
                            response exercises.
                        </p>

                        <p className="text-zinc-200 text-sm md:text-base max-w-3xl mt-4 leading-relaxed">
                            The goal of Neuralis is simple: train your brain to react faster.
                            By combining minimal design with high-speed interaction tests,
                            Neuralis creates a focused environment where players can measure
                            and improve their neural response time.
                        </p>
                    </header>

                    {/* Core Value Grid */}
                    <div className="grid md:grid-cols-2 gap-12 mb-24">
                        <div className="space-y-10">
                            <article>
                                <h3 className="text-xs tracking-[0.4em] text-white uppercase font-bold mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                    The Science
                                </h3>

                                <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
                                    Neuralis measures the time between visual stimulus and player
                                    response. This helps evaluate real reaction speed in
                                    milliseconds. Regular training can strengthen neural
                                    pathways that support faster decision-making and improved
                                    hand-eye coordination.
                                </p>
                            </article>

                            <article>
                                <h3 className="text-xs tracking-[0.4em] text-white uppercase font-bold mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-zinc-700 rounded-full" />
                                    The Ecosystem
                                </h3>

                                <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
                                    Neuralis is developed by <span className="text-white">OMORE Games</span>, a studio focused on creating minimal and high-performance interactive experiences.
                                    The goal is to design simple digital environments where players can challenge
                                    their reaction speed, focus, and cognitive performance through engaging
                                    reflex-based tests.
                                </p>
                            </article>

                            <article>
                                <h3 className="text-xs tracking-[0.4em] text-white uppercase font-bold mb-3">
                                    Why Neuralis
                                </h3>

                                <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
                                    Unlike traditional brain games, Neuralis focuses on pure
                                    reaction training. The interface removes distractions and
                                    presents fast decision challenges that push your reflexes,
                                    accuracy, and mental focus.
                                </p>
                            </article>
                        </div>

                        {/* Training Features Box */}
                        <div className="bg-zinc-900/30 border border-zinc-800 p-8 md:p-10 rounded-sm relative">
                            <div className="absolute top-0 right-0 p-4 opacity-10 font-mono text-[40px] font-black select-none">
                                SYS
                            </div>

                            <h3 className="text-[10px] tracking-[0.5em] text-zinc-200 uppercase font-black mb-10">
                                Core Modules
                            </h3>

                            <div className="space-y-8">
                                {[
                                    {
                                        label: "Reflex Training",
                                        desc: "Improve reaction speed and visual response time."
                                    },
                                    {
                                        label: "Precision Training",
                                        desc: "Develop accuracy under fast decision pressure."
                                    },
                                    {
                                        label: "Memory Training",
                                        desc: "Strengthen pattern recognition and short-term memory."
                                    }
                                ].map((item, index) => (
                                    <div key={index} className="group">
                                        <div className="flex justify-between items-end mb-2">
                                            <span className="text-xs tracking-widest uppercase font-bold text-zinc-300 group-hover:text-blue-400 transition-colors">
                                                {item.label}
                                            </span>
                                            <span className="text-[10px] font-mono text-zinc-600">
                                                0{index + 1}
                                            </span>
                                        </div>

                                        <div className="h-px w-full bg-zinc-800 group-hover:bg-blue-900 transition-colors mb-2" />

                                        <p className="text-[11px] text-zinc-200 uppercase tracking-tighter italic">
                                            {item.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* --- UPCOMING GAMES SECTION --- */}
                    {/* --- UPCOMING GAMES SECTION --- */}
                    <div className="mb-24 pt-16 border-t border-zinc-900">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
                            <div className="flex items-center gap-3">
                                <div className="w-1 h-4 bg-blue-600" />
                                <h3 className="text-[10px] md:text-xs tracking-[0.6em] text-white uppercase font-black whitespace-nowrap">
                                    Future Neural Links
                                </h3>
                            </div>
                            <div className="h-px flex-1 bg-gradient-to-r from-zinc-800 to-transparent hidden md:block ml-10" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800/30 border border-zinc-800/30">

                            {/* AXION - SPEED */}
                            <div className="group relative p-8 bg-[#0d0e10] transition-all duration-500 hover:bg-zinc-950 min-h-[260px] flex flex-col justify-between overflow-hidden">
                                <div>
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <span className="text-[9px] text-red-500/60 font-mono block mb-1">MODULE_01</span>
                                            <h4 className="text-xl font-black tracking-tighter text-zinc-400 group-hover:text-red-500 transition-colors">AXION</h4>
                                        </div>
                                        <span className="text-[7px] px-2 py-0.5 border border-red-500/20 text-red-500 uppercase font-black tracking-tighter">Soon</span>
                                    </div>
                                    <p className="text-[11px] text-zinc-200 uppercase font-bold leading-relaxed tracking-tight group-hover:text-zinc-300 transition-colors">
                                        High-speed target acquisition. Click fast, move faster, and eliminate latency.
                                    </p>
                                </div>
                                <div className="absolute bottom-0 left-0 w-0 h-1 bg-red-600 transition-all duration-500 group-hover:w-full" />
                            </div>

                            {/* SYNCHRO - RHYTHM */}
                            <div className="group relative p-8 bg-[#0d0e10] transition-all duration-500 hover:bg-zinc-950 min-h-[260px] flex flex-col justify-between overflow-hidden">
                                <div>
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <span className="text-[9px] text-purple-500/60 font-mono block mb-1">MODULE_02</span>
                                            <h4 className="text-xl font-black tracking-tighter text-zinc-400 group-hover:text-purple-500 transition-colors">SYNCHRO</h4>
                                        </div>
                                        <span className="text-[7px] px-2 py-0.5 border border-purple-500/20 text-purple-500 uppercase font-black tracking-tighter">Planning</span>
                                    </div>
                                    <p className="text-[11px] text-zinc-200 uppercase font-bold leading-relaxed tracking-tight group-hover:text-zinc-300 transition-colors">
                                        Rhythm-based signal processing. Sync your hand to the pulse of the neural link.
                                    </p>
                                </div>
                                <div className="absolute bottom-0 left-0 w-0 h-1 bg-purple-600 transition-all duration-500 group-hover:w-full" />
                            </div>

                            {/* DUALIS - MULTI-TASK */}
                            <div className="group relative p-8 bg-[#0d0e10] transition-all duration-500 hover:bg-zinc-950 min-h-[260px] flex flex-col justify-between overflow-hidden">
                                <div>
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <span className="text-[9px] text-yellow-500/60 font-mono block mb-1">MODULE_03</span>
                                            <h4 className="text-xl font-black tracking-tighter text-zinc-400 group-hover:text-yellow-500 transition-colors">DUALIS</h4>
                                        </div>
                                        <span className="text-[7px] px-2 py-0.5 border border-yellow-500/20 text-yellow-500 uppercase font-black tracking-tighter">Planning</span>
                                    </div>
                                    <p className="text-[11px] text-zinc-200 uppercase font-bold leading-relaxed tracking-tight group-hover:text-zinc-300 transition-colors">
                                        Split-brain multitasking. Handle two distinct data streams without cognitive failure.
                                    </p>
                                </div>
                                <div className="absolute bottom-0 left-0 w-0 h-1 bg-yellow-600 transition-all duration-500 group-hover:w-full" />
                            </div>

                            {/* STAMINA - FOCUS */}
                            {/* <div className="group relative p-8 bg-[#0d0e10] transition-all duration-500 hover:bg-zinc-950 min-h-[260px] flex flex-col justify-between overflow-hidden">
      <div>
        <div className="flex justify-between items-start mb-6">
          <div>
            <span className="text-[9px] text-blue-500/60 font-mono block mb-1">MODULE_04</span>
            <h4 className="text-xl font-black tracking-tighter text-zinc-400 group-hover:text-blue-500 transition-colors">STAMINA</h4>
          </div>
          <span className="text-[7px] px-2 py-0.5 border border-blue-500/20 text-blue-500 uppercase font-black tracking-tighter">Planning</span>
        </div>
        <p className="text-[11px] text-zinc-200 uppercase font-bold leading-relaxed tracking-tight group-hover:text-zinc-300 transition-colors">
          Long-form focus testing. Maintain peak reaction speed over extended neural stress.
        </p>
      </div>
      <div className="absolute bottom-0 left-0 w-0 h-1 bg-blue-600 transition-all duration-500 group-hover:w-full" />
    </div> */}

                            {/* DISCOVERY - VIEW MORE */}
                            <Link
                                to="/gamelibrary"
                                className="group relative p-8 bg-[#0d0e10] transition-all duration-500 hover:bg-zinc-950 min-h-[260px] flex flex-col justify-between overflow-hidden border-l border-zinc-900 md:border-l-0"
                            >
                                <div>
                                    <div className="flex justify-between items-start mb-6">
                                        <div>
                                            <span className="text-[9px] text-zinc-200 font-mono block mb-1">ALL_GAMES</span>
                                            <h4 className="text-xl font-black tracking-tighter text-zinc-400 group-hover:text-white transition-colors">VIEW MORE</h4>
                                        </div>
                                        <div className="w-8 h-8 rounded-full border border-zinc-800 flex items-center justify-center group-hover:border-white transition-colors">
                                            <span className="text-lg font-light text-zinc-200 group-hover:text-white">+</span>
                                        </div>
                                    </div>

                                    {/* Simple, clear language */}
                                    <p className="text-[11px] text-zinc-200 uppercase font-bold leading-relaxed tracking-tight group-hover:text-zinc-300 transition-colors">
                                        Check out our full collection. Play more games, test your speed, and try new challenges in the OMORE lab.
                                    </p>
                                </div>

                                {/* Visual background text */}
                                <div className="absolute -bottom-4 -right-2 text-[60px] font-black text-white/[0.02] pointer-events-none select-none italic">
                                    MORE
                                </div>

                                <div className="absolute bottom-0 left-0 w-0 h-1 bg-white transition-all duration-500 group-hover:w-full" />
                            </Link>

                        </div>
                    </div>
                    {/* Footer */}
                    {/* <footer className="pt-16 pb-8 border-t border-zinc-900 flex flex-col gap-12">

                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-10">

                            <div className="flex items-center gap-8">
                                <div className="flex flex-col">
                                    <p className="text-[8px] tracking-[0.5em] uppercase font-black text-zinc-200 mb-2">
                                        Published By
                                    </p>
                                    <h4 className="text-xs tracking-[0.2em] font-bold text-white uppercase">
                                        OMORE GAMES
                                    </h4>
                                </div>

                                <div className="h-10 w-px bg-zinc-800 hidden sm:block" />

                                <div className="flex flex-col">
                                    <p className="text-[8px] tracking-[0.5em] uppercase font-black text-zinc-200 mb-2">
                                        Project
                                    </p>
                                    <h4 className="text-xs tracking-[0.2em] font-bold text-blue-500 uppercase">
                                        NEURALIS_v1.0.4
                                    </h4>
                                </div>
                            </div>

                            <nav className="flex flex-wrap items-center gap-x-8 gap-y-4">
                                {[
                                    { name: "About Studio", path: "/aboutomore" },
                                    { name: "Game Library", path: "/moregames" },
                                    { name: "Support", path: "/support" },
                                    { name: "Privacy", path: "/privacy" },
                                ].map((link) => (
                                    <Link
                                        key={link.name}
                                        to={link.path}
                                        className="text-[10px] tracking-[0.2em] uppercase text-zinc-200 hover:text-white transition-colors"
                                    >
                                        {link.name}
                                    </Link>
                                ))}
                            </nav>
                        </div>

                        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-zinc-900/50 gap-4">
                            <p className="text-[9px] tracking-widest text-zinc-600 uppercase">
                                &copy; {new Date().getFullYear()} OMORE GAMES. ALL RIGHTS RESERVED.
                            </p>

                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                                    <span className="text-[9px] tracking-widest text-zinc-600 uppercase">System Online</span>
                                </div>
                                <span className="text-zinc-800">|</span>
                                <p className="text-[9px] tracking-widest text-zinc-600 uppercase">Designed for High-Precision</p>
                            </div>
                        </div>
                    </footer> */}
                </div>
            </section>
        </>
    );
}